import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '';
const PROMPT_MAX = 2000; // must match PROMPT_MAX in backend/routes/jobs.js
const ACCENT = '#00e896';

const today = new Date().toLocaleDateString('en-US', {
  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
});

export default function LandingPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const chars = prompt.length;
  const overLimit = chars > PROMPT_MAX;

  const ACCEPTED = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  ];

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (f && !ACCEPTED.includes(f.type)) {
      setError('Unsupported file. Upload a PDF, image, or Word (.docx) file.');
      return;
    }
    setError('');
    setFile(f || null);
  };

  const handleSubmit = async () => {
    setError('');
    if (!file) { setError('Upload your resume file first.'); return; }
    if (!prompt.trim()) { setError('Write a prompt before searching.'); return; }
    if (overLimit) { setError(`Prompt is over the ${PROMPT_MAX} character limit.`); return; }

    setLoading(true);
    const form = new FormData();
    form.append('resume', file);
    form.append('userPrompt', prompt.trim());

    try {
      const { data } = await axios.post(`${API_BASE}/api/jobs/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Hand the results to the jobs page via router state
      navigate('/jobs', { state: { result: data } });
    } catch (e) {
      setError(e.response?.data?.error || 'Something went wrong. Is the backend running?');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.root}>
      <div style={styles.noise} />
      <div style={styles.grid} />

      <div style={styles.inner}>

        {/* Header */}
        <header style={styles.header} className="fade-up">
          <div style={styles.badge}>
            <span style={styles.dot} />
            AI-Powered · Your Prompt
          </div>
          <h1 style={styles.title}>
            <span style={styles.titleName}>JobHunter</span>
            <span style={styles.titleAI}> AI</span>
          </h1>
          <p style={styles.subtitle}>Anand Pandey &mdash; {today}</p>
          <p style={styles.desc}>
            Upload a resume and write your own prompt. Claude reads both and returns
            matched, scored job opportunities.
          </p>
        </header>

        {/* Two-card row */}
        <div style={styles.cardsRow}>

          {/* Card 1 — Upload Resume */}
          <div style={styles.card} className="fade-up-2">
            <div style={styles.cardGlow(ACCENT)} />
            <div style={styles.cardTag(ACCENT, '#001f0f')}>● STEP 1 · RESUME</div>
            <div style={styles.cardIcon}>📄</div>
            <h2 style={{ ...styles.cardTitle, color: ACCENT }}>Upload Resume</h2>
            <p style={styles.cardDesc}>PDF, image/photo, or Word (.docx) — one file, max 8 MB.</p>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,image/*"
              onChange={onPickFile}
              style={{ display: 'none' }}
            />
            <button
              style={{ ...styles.uploadBtn, borderColor: ACCENT, color: ACCENT }}
              onClick={() => fileInputRef.current?.click()}
            >
              {file ? 'Change File' : 'Choose File'}
            </button>

            <div style={styles.fileName}>
              {file
                ? <span style={{ color: ACCENT }}>✓ {file.name}</span>
                : <span style={{ color: '#3a4460' }}>No file selected</span>}
            </div>
          </div>

          {/* Card 2 — Your Prompt */}
          <div style={styles.card} className="fade-up-3">
            <div style={styles.cardGlow('#7c6fff')} />
            <div style={styles.cardTag('#7c6fff', '#0d0a2a')}>● STEP 2 · PROMPT</div>
            <div style={styles.cardIcon}>✍️</div>
            <h2 style={{ ...styles.cardTitle, color: '#7c6fff' }}>Your Prompt</h2>
            <p style={styles.cardDesc}>Tell Claude what kind of jobs to find.</p>

            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={
                'e.g. Find 20 entry-level backend engineer roles in the US posted in the last 2 days. Prefer remote or Boston. Score how well I fit each one.'
              }
              rows={6}
              style={{
                ...styles.textarea,
                borderColor: overLimit ? '#ff5c5c' : '#1a1e35',
              }}
            />
            <div style={styles.counterRow}>
              <span style={{ color: overLimit ? '#ff5c5c' : '#3a4460', fontSize: 11 }}>
                {chars} / {PROMPT_MAX}
              </span>
            </div>
          </div>

        </div>

        {error && <p style={styles.error}>⚠ {error}</p>}

        {/* Submit */}
        <button
          style={{
            ...styles.submitBtn,
            opacity: loading ? 0.6 : 1,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
          onClick={handleSubmit}
          disabled={loading}
          className="fade-up-4"
        >
          {loading ? 'Analyzing resume…' : 'Find Jobs →'}
        </button>

        <footer style={styles.footer} className="fade-up-5">
          Built by Anand Pandey
        </footer>
      </div>
    </div>
  );
}

const styles = {
  root: {
    minHeight: '100vh', background: '#07080f', position: 'relative',
    overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  noise: {
    position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'repeat', backgroundSize: '128px',
  },
  grid: {
    position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
    backgroundImage: `linear-gradient(#0d1020 1px, transparent 1px), linear-gradient(90deg, #0d1020 1px, transparent 1px)`,
    backgroundSize: '60px 60px', opacity: 0.6,
  },
  inner: {
    position: 'relative', zIndex: 1, width: '100%', maxWidth: 900,
    margin: '0 auto', padding: '48px 24px', display: 'flex',
    flexDirection: 'column', alignItems: 'center', gap: 32,
  },
  header: { textAlign: 'center', maxWidth: 600 },
  badge: {
    display: 'inline-flex', alignItems: 'center', gap: 6, background: '#0d1020',
    border: '1px solid #1a2040', color: '#5a6a90', fontSize: 10, letterSpacing: 2,
    textTransform: 'uppercase', padding: '5px 14px', borderRadius: 20, marginBottom: 20,
  },
  dot: {
    width: 6, height: 6, borderRadius: '50%', background: ACCENT,
    display: 'inline-block', animation: 'pulse 2s infinite',
  },
  title: {
    fontFamily: "'Syne', sans-serif", fontSize: 'clamp(42px, 8vw, 72px)',
    fontWeight: 800, lineHeight: 1, marginBottom: 12,
  },
  titleName: { color: '#ffffff' },
  titleAI: { color: ACCENT },
  subtitle: {
    color: '#3a4a6a', fontSize: 12, letterSpacing: 1,
    textTransform: 'uppercase', marginBottom: 16,
  },
  desc: { color: '#5a6a90', fontSize: 14, lineHeight: 1.7 },

  cardsRow: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: 20, width: '100%',
  },
  card: {
    position: 'relative', overflow: 'hidden', background: '#0d0f1c',
    border: '1px solid #1a1e35', borderRadius: 16, padding: '32px 28px',
    display: 'flex', flexDirection: 'column', gap: 16, textAlign: 'left',
  },
  cardGlow: (color) => ({
    position: 'absolute', top: -60, right: -60, width: 200, height: 200,
    borderRadius: '50%', background: color, opacity: 0.04,
    pointerEvents: 'none', filter: 'blur(40px)',
  }),
  cardTag: (color, bg) => ({
    display: 'inline-block', fontSize: 9, fontWeight: 700, letterSpacing: 2,
    textTransform: 'uppercase', color, background: bg,
    border: `1px solid ${color}33`, padding: '3px 10px', borderRadius: 4,
    width: 'fit-content',
  }),
  cardIcon: { fontSize: 32 },
  cardTitle: { fontFamily: "'Syne', sans-serif", fontSize: 24, fontWeight: 800 },
  cardDesc: { color: '#5a6a90', fontSize: 12, lineHeight: 1.6 },

  uploadBtn: {
    background: 'transparent', border: '1px solid', borderRadius: 8,
    padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
    width: 'fit-content',
  },
  fileName: { fontSize: 12, wordBreak: 'break-all' },

  textarea: {
    width: '100%', background: '#07080f', border: '1px solid',
    borderRadius: 8, color: '#e6ecff', fontSize: 13, lineHeight: 1.6,
    padding: '12px 14px', resize: 'vertical',
    fontFamily: "'JetBrains Mono', monospace", boxSizing: 'border-box',
  },
  counterRow: { display: 'flex', justifyContent: 'flex-end', marginTop: -8 },

  error: { color: '#ff5c5c', fontSize: 13, textAlign: 'center' },

  submitBtn: {
    background: ACCENT, color: '#02150c', border: 'none', borderRadius: 10,
    padding: '14px 48px', fontSize: 15, fontWeight: 800,
    fontFamily: "'Syne', sans-serif", letterSpacing: 0.5,
  },
  footer: {
    color: '#2a3450', fontSize: 11, letterSpacing: 1, textAlign: 'center',
  },
};